import * as m from '@/paraglide/messages.js';
import { z } from 'zod';

const errorMessages: Record<string, () => string> = {
  DRIVE_NOT_CONNECTED: m.api_drive_not_connected,
  DRIVE_FOLDER_REQUIRED: m.api_drive_folder_required,
  DRIVE_ITEM_UNAVAILABLE: m.api_drive_item_unavailable,
  DRIVE_RECONNECT_REQUIRED: m.api_drive_reconnect_required,
  DRIVE_ACCESS_DENIED: m.api_drive_access_denied,
  DRIVE_API_DISABLED: m.api_drive_api_disabled,
  DRIVE_UNAVAILABLE: m.api_drive_unavailable,
  DRIVE_SPREADSHEET_REQUIRED: m.api_drive_spreadsheet_required,
  DRIVE_FOLDER_INVALID: m.api_drive_folder_invalid,
  DRIVE_WORKBOOK_REQUIRED: m.api_drive_workbook_required,
  DRIVE_TAB_NOT_FOUND: m.api_drive_tab_not_found,
  TRANSFER_PAYER_UNCONFIGURED: m.api_transfer_payer_unconfigured,
  TRANSFER_PAYER_RIB_INVALID: m.api_transfer_payer_rib_invalid,
  TRANSFER_SUPPLIER_RIB_INVALID: m.api_transfer_supplier_rib_invalid,
  TRANSFER_AMOUNT_INVALID: m.api_transfer_amount_invalid,
  TRANSFER_ORDER_FAILED: m.api_transfer_order_failed,
  MAILBOX_SCAN_RUNNING: m.api_mailbox_scan_running,
  MAILBOX_RECONNECT_REQUIRED: m.api_mailbox_reconnect_required,
  INVOICE_READER_NOT_CONFIGURED: m.api_invoice_reader_not_configured,
  INVITATION_DELIVERY_NOT_CONFIGURED: m.api_invitation_delivery_not_configured,
  INVOICE_PDF_TOO_LARGE: m.mailbox_pdf_too_large,
  IMPORT_ALREADY_RUNNING: m.api_import_already_running,
  UNAUTHENTICATED: m.api_unauthenticated,
  FORBIDDEN: m.api_forbidden,
  NOT_FOUND: m.reference_not_found_description,
  VALIDATION_FAILED: m.api_validation,
  BAD_REQUEST: m.api_validation,
  CONFLICT: m.api_conflict,
  STALE_VERSION: m.reference_stale_record,
  THROTTLED: m.api_throttled,
  USAGE_LIMIT_REACHED: m.api_usage_limit,
  PERSON_INACTIVE: m.api_person_inactive,
  LAST_ADMIN_PROTECTED: m.api_last_admin,
  INVITATION_EXPIRED: m.api_invitation_expired,
  INVITATION_REVOKED: m.api_invitation_revoked,
  INVITATION_ALREADY_ACCEPTED: m.api_invitation_accepted,
  INVALID_CREDENTIALS: m.api_invalid_credentials,
  SSO_NOT_CONFIGURED: m.api_sso_unavailable,
  METHOD_NOT_ALLOWED: m.api_method_not_allowed,
  LEDGER_TAB_LAYOUT: m.api_ledger_tab_layout,
  DRIVE_SETUP_INCOMPLETE: m.api_drive_setup_incomplete,
  INVOICE_TRANSFER_DATED: m.api_invoice_transfer_dated,
  INVOICE_TRANSFER_DATED_IN_LEDGER: m.api_invoice_transfer_dated_in_ledger,
  LEDGER_UNREADABLE: m.api_ledger_unreadable,
  PAYER_ACCOUNT_IN_USE: m.api_payer_account_in_use,
};

export function apiErrorMessage(
  code?: string,
  fallback: () => string = m.reference_request_failed,
): string {
  return (code ? errorMessages[code] : undefined)?.() ?? fallback();
}

const fieldMessages: Record<string, () => string> = {
  name: m.validation_name_length,
  bankIdentifier: m.supplier_bank_identifier_invalid,
  code: m.validation_code_length,
  description: m.validation_description_length,
  logo: m.validation_logo_length,
  clientId: m.validation_choose_client,
  personId: m.project_assignment_choose_people_error,
  personIds: m.project_assignment_choose_people_error,
  teamPersonIds: m.team_scope_members_invalid,
  projectId: m.assignment_choose_one_project,
  projectIds: m.assignment_choose_one_project,
  validFrom: m.validation_valid_calendar_date,
  validTo: m.validation_valid_last_date,
  effectiveOn: m.validation_valid_calendar_date,
  effectiveFrom: m.validation_valid_calendar_date,
  effectiveTo: m.validation_valid_last_date,
  query: m.validation_search_length,
  reason: m.validation_reason_length,
  limit: m.validation_history_page_size,
  version: m.reference_stale_record,
  dailyRate: m.validation_daily_rate,
  currency: m.validation_currency,
  rates: m.validation_rate_periods,
  minimumPeriod: m.client_rates_month_error,
  continuousPeriods: m.client_rates_continuity_error,
};

const fieldErrorDetails = z.object({ fieldErrors: z.record(z.string(), z.unknown()) });

export function apiFieldErrors(
  details: unknown,
  overrides: Record<string, () => string> = {},
  preservePaths = false,
): Record<string, string> {
  const errors = fieldErrorDetails.safeParse(details).data?.fieldErrors;
  if (!errors) return {};
  return Object.fromEntries(
    Object.keys(errors).map((path) => {
      const parts = path.split('.');
      const field = parts[0]!;
      const leaf = parts.findLast((part) => !/^\d+$/.test(part)) ?? field;
      const message =
        overrides[leaf] ?? fieldMessages[leaf] ?? overrides[field] ?? fieldMessages[field];
      return [preservePaths ? path : field, (message ?? m.validation_field)()];
    }),
  );
}
